import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DropboxConnection {
  id: string;
  status: string;
  last_sync_at: string | null;
}

export function useDropboxConnection() {
  const [connection, setConnection] = useState<DropboxConnection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ imported: 0, total: 0 });

  const fetchConnection = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("connected_accounts")
        .select("id, status, last_sync_at")
        .eq("user_id", user.id)
        .eq("provider", "dropbox")
        .maybeSingle();

      if (error) {
        console.error("Error fetching Dropbox connection:", error);
      } else {
        setConnection(data);
      }
    } catch (err) {
      console.error("Failed to fetch Dropbox connection:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnection();
  }, [fetchConnection]);

  const connect = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("dropbox-auth");

      if (error) {
        toast.error("Erreur de connexion Dropbox");
        console.error("Dropbox auth error:", error);
        return;
      }

      if (data?.success) {
        toast.success("Dropbox connecté avec succès");
        await fetchConnection();
      } else if (data?.authUrl) {
        // Fallback to OAuth flow if needed
        window.location.href = data.authUrl;
      }
    } catch (err) {
      console.error("Failed to connect to Dropbox:", err);
      toast.error("Impossible de se connecter à Dropbox");
    }
  }, [fetchConnection]);

  const disconnect = useCallback(async () => {
    if (!connection) return;

    try {
      const { error } = await supabase
        .from("connected_accounts")
        .delete()
        .eq("id", connection.id);

      if (error) throw error;

      setConnection(null);
      toast.success("Dropbox déconnecté");
    } catch (err) {
      console.error("Failed to disconnect Dropbox:", err);
      toast.error("Erreur lors de la déconnexion");
    }
  }, [connection]);

  const sync = useCallback(async () => {
    if (!connection || isSyncing) return;

    setIsSyncing(true);
    setSyncProgress({ imported: 0, total: 0 });

    try {
      const { data, error } = await supabase.functions.invoke("dropbox-sync");

      if (error) {
        if (error.message?.includes("Token expired") || error.message?.includes("401")) {
          toast.error("Session Dropbox expirée", {
            description: "Veuillez vous reconnecter à Dropbox",
          });
          setConnection(prev => prev ? { ...prev, status: "expired" } : null);
          return;
        }
        throw error;
      }

      if (data?.error) {
        toast.error("Erreur de synchronisation", {
          description: data.error,
        });
        return;
      }

      setSyncProgress({ imported: data.imported, total: data.total });
      
      toast.success("Synchronisation terminée", {
        description: `${data.imported} document(s) importé(s)`,
      });

      // Refresh connection data
      await fetchConnection();
    } catch (err) {
      console.error("Dropbox sync error:", err);
      toast.error("Erreur lors de la synchronisation");
    } finally {
      setIsSyncing(false);
    }
  }, [connection, isSyncing, fetchConnection]);

  return {
    connection,
    isConnected: connection?.status === "connected",
    isExpired: connection?.status === "expired",
    isLoading,
    isSyncing,
    syncProgress,
    connect,
    disconnect,
    sync,
    refresh: fetchConnection,
  };
}
